import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { applyDirection } from "../i18n";
import { updateLanguage } from "../api/auth";
import { useAuth } from "./AuthContext";

// One-time migration: the old default was dark, so devices still carry "dark" in storage.
// Light is now the agreed default — reset once, then respect the user's own choice.
const initialTheme = () => {
  if (!localStorage.getItem("themeDefault.v2")) {
    localStorage.setItem("themeDefault.v2", "light");
    localStorage.setItem("themeMode", "light");
    return "light";
  }
  return localStorage.getItem("themeMode") || "light";
};

const PrefsContext = createContext(null);

export const PrefsProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { user, patchUser } = useAuth();
  const [themeMode, setThemeMode] = useState(initialTheme);
  const [isDay, setIsDay] = useState(null);
  const serverLangApplied = useRef(false);

  const theme = themeMode === "auto" ? (isDay === false ? "dark" : "light") : themeMode;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("themeMode", themeMode);
  }, [theme, themeMode]);

  // The saved language is applied once, on first load; manual switches always win afterwards.
  useEffect(() => {
    if (serverLangApplied.current || !user || !user.language) return;
    serverLangApplied.current = true;
    if (user.language !== i18n.language) {
      i18n.changeLanguage(user.language);
      applyDirection(user.language);
      localStorage.setItem("lang", user.language);
    }
  }, [user, i18n]);

  const setLang = useCallback(
    async (lang) => {
      i18n.changeLanguage(lang);
      applyDirection(lang);
      localStorage.setItem("lang", lang);
      if (user) {
        patchUser({ language: lang });
        try {
          await updateLanguage(lang);
        } catch {
          /* preference stays local if request fails */
        }
      }
    },
    [i18n, user, patchUser]
  );

  const toggleTheme = useCallback(() => setThemeMode((m) => (m === "dark" ? "light" : "dark")), []);
  const reportDaylight = useCallback((value) => setIsDay(value), []);

  return (
    <PrefsContext.Provider
      value={{ theme, themeMode, setThemeMode, toggleTheme, reportDaylight, lang: i18n.language, setLang }}
    >
      {children}
    </PrefsContext.Provider>
  );
};

export const usePrefs = () => useContext(PrefsContext);
